import { Link } from "react-router-dom";
import { Reveal, Eyebrow, BracketLink, Marker } from "@/components/atoms";
import { IMAGES } from "@/data/site";

const ROWS = [
  { sku: "HR-1600-SW", od: '1/4" – 1"', temp: "1600°F", watt: "300 W/in²", volt: "120–480 V" },
  { sku: "HR-0800-NS", od: '1/4" – 1"', temp: "800°F", watt: "150 W/in²", volt: "120–480 V" },
  { sku: "HR-SQ-MILL", od: '3/8" – 3/4"', temp: "1200°F", watt: "220 W/in²", volt: "120–480 V" },
  { sku: "TC-TYPEK", od: "—", temp: "2300°F", watt: "—", volt: "—", note: "sensing" },
];

export default function MockupProduct() {
  return (
    <div className="bg-bone min-h-screen text-navy">
      <div className="bg-navy no-print sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center justify-between">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-paper/70" data-testid="mockup-product-ribbon">REDESIGN MOCKUP 2 / 2 — PRODUCT LINE PAGE</p>
          <Link to="/" data-testid="mockup-product-back" className="font-mono text-[10px] tracking-[0.2em] uppercase text-paper/70 hover:text-ember transition-colors">&larr; PROPOSAL</Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim" data-testid="mockup-product-breadcrumb">PRODUCTS / CARTRIDGE HEATERS</p>
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 items-start mt-8">
          <div>
            <Reveal><Eyebrow>SWAGED · NON-SWAGED · SQUARE</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-body font-medium tracking-[-0.035em] leading-[1.02] text-4xl lg:text-6xl mt-6">Cartridge Heaters</h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-mutedx text-lg mt-6 leading-relaxed max-w-xl">
                Precision-ground for tooling, platens and dies. Every variant ships with its spec sheet inline —
                temperature ceiling, watt density and voltage — so buyers self-serve before they ever call.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="flex flex-wrap gap-2 mt-8">
              {["≤ 1600°F", "≤ 900°F", "≤ 1200°F"].map((f) => (
                <span key={f} className="font-mono text-[10px] tracking-[0.15em] uppercase border border-linec bg-white rounded-sm px-3 py-2 text-mutedx">{f}</span>
              ))}
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative overflow-hidden border border-linec">
              <img src={IMAGES.metalRed} alt="Cartridge heater element" className="w-full h-[300px] object-cover" />
              <Marker className="top-3 right-3" />
              <p className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-paper/80">FIG. 02 — SHEATH DETAIL</p>
            </div>
          </Reveal>
        </div>
      </div>

      {/* spec table */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <Reveal><Eyebrow>LINE SPEC SHEET</Eyebrow></Reveal>
        <Reveal delay={0.05} className="mt-8 border border-linec bg-white overflow-hidden">
          <div className="grid grid-cols-[1.2fr_repeat(4, 1fr)] md:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-2 px-6 py-4 border-b border-linec font-mono text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-dim">
            <span>SKU</span><span>OD RANGE</span><span>MAX TEMP</span><span>WATT DENSITY</span><span>VOLTAGE</span>
          </div>
          {ROWS.map((r) => (
            <div key={r.sku} data-testid={`mockup-spec-row-${r.sku}`}
              className="grid grid-cols-[1.2fr_repeat(4,1fr)] md:grid-cols-[1.4fr_1fr_1fr_1fr_1fr] gap-2 px-6 py-5 border-b border-linec last:border-b-0 items-center hover:bg-ember/5 transition-colors">
              <span className="font-mono text-xs text-ember font-bold">{r.sku}</span>
              <span className="text-sm text-mutedx">{r.od}</span>
              <span className="font-mono text-sm text-navy">{r.temp}</span>
              <span className="font-mono text-sm text-mutedx">{r.watt}</span>
              <span className="text-right md:text-left"><Link to="/demo" data-testid={`mockup-spec-rfq-${r.sku}`}
                className="font-mono text-[10px] tracking-[0.15em] uppercase bg-ember text-white font-bold px-3.5 py-2 rounded-sm hover:bg-glow transition-colors">+ RFQ</Link></span>
            </div>
          ))}
        </Reveal>

        <Reveal delay={0.1} className="mt-10 border border-ember bg-ember/5 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="mockup-quote-band">
          <div>
            <h3 className="font-body font-semibold text-2xl text-navy">Need this line quoted?</h3>
            <p className="text-mutedx text-sm mt-2">The live demo portal carries these SKUs — search, set quantities, send.</p>
          </div>
          <BracketLink to="/demo" primary testid="mockup-quote-cta">
            Try it on the live demo &rarr;
          </BracketLink>
        </Reveal>
      </section>

      <footer className="bg-navy">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row justify-between gap-4">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-paper/50">BACKER MARATHON · PRODUCT LINE: CARTRIDGE HEATERS</p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-paper/50">MOCKUP — NOT FINAL COPY</p>
        </div>
      </footer>
    </div>
  );
}
